import { useForm } from "react-hook-form";
import { DMModal, DMModalContent } from "../../../shared/DMModal";
import Button from "../../../student/components/generic/button";
import { formatNumber } from "../../utils";
import { useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { executeQuery } from "../../../utils";
import { useParams } from "react-router-dom";
import { useDeltaToastContext } from "../../../shared/contexts/ToasterContext";

interface FormInputs {
  snnStudentCount: number;
}

export const ConfirmNumStudentsModal: React.FC<{
  previousNumStudents: number | undefined;
  grades: string | undefined;
  school: string | undefined;
  visible: boolean;
  onSaved: () => void;
}> = ({
  previousNumStudents,
  grades,
  school = "this school",
  visible,
  onSaved,
}) => {
  const { renewalId } = useParams();
  const toastContext = useDeltaToastContext();

  const {
    formState: { errors },
    handleSubmit,
    register,
    setValue,
  } = useForm<FormInputs>();

  const { mutate, isPending } = useMutation({
    mutationFn: (data: FormInputs) => {
      return executeQuery({
        path: `/public/renewal/data/${encodeURIComponent(renewalId || "")}`,
        method: "post",
        requestBody: { ...data },
      });
    },
  });

  const onSubmit = (data: FormInputs) => {
    mutate(data, {
      onSuccess: () => onSaved(),
      onError: () => {
        toastContext.addToast({
          message: "There was an error submitting your request.",
          status: "Error",
        });
      },
    });
  };

  useEffect(() => {
    if (previousNumStudents) {
      setValue("snnStudentCount", previousNumStudents);
    }
  }, [previousNumStudents, setValue]);

  return (
    <DMModal
      className="max-w-[700px] !pb-12 !pt-10"
      visible={visible}
      closeable={false}
    >
      <DMModalContent className="items-center !gap-10">
        <header className="flex w-full flex-col items-center">
          <h3 className="font-serif text-2xl font-bold">
            {previousNumStudents
              ? "Confirm the Number of Students"
              : "Enter the Number of Students"}
          </h3>
          <p>
            {grades
              ? `entrolled in grades ${grades} at ${school}`
              : `entrolled at ${school}`}
          </p>
        </header>

        <form
          className="flex w-full max-w-xs flex-col gap-10"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="flex flex-col gap-1">
            <label className="text-sm font-bold" htmlFor="snnStudentCount">
              Number of Students
            </label>
            <input
              className="h-10 rounded border border-dm-gray-200 px-2 py-0 text-sm"
              type="number"
              id="snnStudentCount"
              min="1"
              {...register("snnStudentCount", {
                valueAsNumber: true,
                required: "This field is required",
                min: { value: 1, message: "Enter 1 or more students" },
              })}
            />
            {errors.snnStudentCount ? (
              <p className="text-xs text-dm-error-500">
                {errors.snnStudentCount.message}
              </p>
            ) : previousNumStudents ? (
              <p className="flex items-center gap-2 text-xs text-dm-charcoal-500">
                <i className="fal fa-info-circle" />
                Last year there were approximately{" "}
                {formatNumber(previousNumStudents)} students in our records for
                your school.
              </p>
            ) : (
              <></>
            )}
          </div>
          <Button className="text-sm font-bold" isLoading={isPending}>
            Confirm
          </Button>
        </form>
      </DMModalContent>
    </DMModal>
  );
};
